import isEqual from 'lodash/isEqual';
import H from '@here/maps-api-for-javascript';
import { PANEL_WIDTH_PX, PADDING_PERCENT } from './markerConstants';
import {
  filterDepot,
  isActivityToDisplay,
  isPointDepot,
  isPointReturnLocation,
  featureFilterCluster,
} from '../../../../utils/map/MapFeatureFilters';

export function fitBoundsWithOffset(map, bounds) {
  const mapElement = map.getElement();
  const totalWidth = mapElement.offsetWidth;
  const totalHeight = mapElement.offsetHeight;

  const paddingLeft = totalWidth * PADDING_PERCENT + PANEL_WIDTH_PX / 2;
  const paddingRight = totalWidth * PADDING_PERCENT;
  const paddingTop = totalHeight * PADDING_PERCENT;
  const paddingBottom = totalHeight * PADDING_PERCENT;

  const topLat = bounds.getTop();
  const bottomLat = bounds.getBottom();
  const leftLng = bounds.getLeft();
  const rightLng = bounds.getRight();

  const topLeft = map.geoToScreen({ lat: topLat, lng: leftLng });
  const bottomRight = map.geoToScreen({ lat: bottomLat, lng: rightLng });

  const newTopLeft = map.screenToGeo(topLeft.x - paddingLeft, topLeft.y - paddingTop);
  const newBottomRight = map.screenToGeo(
    bottomRight.x + paddingRight,
    bottomRight.y + paddingBottom,
  );

  const expandedBounds = new H.geo.Rect(
    newBottomRight.lat,
    newTopLeft.lng,
    newTopLeft.lat,
    newBottomRight.lng,
  );

  map.getViewModel().setLookAtData({ bounds: expandedBounds }, true);
}

export function enhanceGeoJSONFeatures(geoJSON, orders, display = {}, user, routingData) {
  if (!geoJSON?.geo?.features) return geoJSON;

  const filteredFeatures = filterDepot(geoJSON, false);
  const jobFeatures = filteredFeatures.filter(
    (f) =>
      f.geometry?.type === 'Point' &&
      !isActivityToDisplay(f) &&
      !isPointDepot(f) &&
      !isPointReturnLocation(f) &&
      featureFilterCluster(f, display, routingData, user?.mapSettings?.showStraightRouteLines),
  );

  return {
    ...geoJSON,
    geo: {
      ...geoJSON.geo,
      features: jobFeatures.map((feature) => {
        if (feature.properties) {
          const order = orders?.find((o) => o.InternalID === feature.properties.id);
          const isEditing = order?.editing || false;
          const isPickup = feature.properties.isPickup || false;
          const highPriority = feature.properties.highPriority || false;
          const parkingId = order?.ParkingId || null;

          return {
            ...feature,
            properties: {
              ...feature.properties,
              isEditing,
              isPickup,
              highPriority,
              parkingId,
            },
          };
        }
        return feature;
      }),
    },
  };
}

export function arePropsEqual(prevProps, nextProps) {
  if (prevProps.map !== nextProps.map) return false;
  if (prevProps.ui !== nextProps.ui) return false;
  if (prevProps.user !== nextProps.user) return false;
  if (prevProps.oAuth !== nextProps.oAuth) return false;
  if (prevProps.geoJSON === nextProps.geoJSON) return true;

  const prevFeatures = prevProps.geoJSON?.geo?.features || [];
  const nextFeatures = nextProps.geoJSON?.geo?.features || [];

  if (prevFeatures.length !== nextFeatures.length) return false;

  const getMarkerData = (features) =>
    features
      .filter((f) => f.geometry?.type === 'Point')
      .map((f) => ({
        id: f.properties?.id,
        coords: f.geometry?.coordinates,
        isEditing: f.properties?.isEditing,
        isPickup: f.properties?.isPickup,
        highPriority: f.properties?.highPriority,
        parkingId: f.properties?.parkingId,
      }));

  return isEqual(getMarkerData(prevFeatures), getMarkerData(nextFeatures));
}

export function fitBoundsFromBbox(map, bbox) {
  if (!bbox || bbox.length !== 4) return;
  const [west, south, east, north] = bbox;
  if (
    !Number.isFinite(west) ||
    !Number.isFinite(south) ||
    !Number.isFinite(east) ||
    !Number.isFinite(north) ||
    west >= east ||
    south >= north
  )
    return;
  fitBoundsWithOffset(map, new H.geo.Rect(north, west, south, east));
}
